import { CommentIcon, EditIcon, EmailIcon, PhoneIcon, UserIcon } from "@xola/icons";
import { Phone, Tooltip } from "@xola/ui-kit";
import { isOtherSellerPurchase, isPartnerPurchase } from "common";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { Conversation, Permission, Purchase, PurchaseItem, Traveler } from "types";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { useSourceMessagingPlugin } from "../../Common/SourceIcon";
import { MessagePurchaseModal } from "../../PurchaseModification/MessagePurchaseModal";
import { PurchaseContactInfoDrawer } from "./PurchaseContactInfoDrawer";
import { api } from "@/api/api";
import { EditedFormValues, EditPrimaryContactModal } from "@/components/Customers/Contacts/EditPrimaryContactModal";
import { MergeContactsModal } from "@/components/Customers/Contacts/MergeContactsModal";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { usePostCheckinWarningModal } from "@/hooks/usePostCheckinWarningModal";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { isAffiliate } from "@/models/User";
import { getSeller } from "@/utils/getSeller";

interface PurchaseContactInformationProps {
    title: string;
    contactId?: string;
    purchase?: Purchase;
    item?: PurchaseItem;
    recipientName?: string;
    recipientEmail?: string;
    onContactInfoClick?: () => void;
}

interface ContactInfoDrawerProps {
    contact: Traveler;
    visible: boolean;
    toggle: () => void;
}

export const PurchaseContactInformation = ({
    title,
    contactId,
    purchase,
    item,
    recipientName,
    recipientEmail,
    onContactInfoClick,
}: PurchaseContactInformationProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [visible, { toggle }] = useVisibility(false);
    const { PostCheckInWarningModal, withPostCheckInWarning } = usePostCheckinWarningModal(item);

    const {
        isVisible: isVisibleEditContactModal,
        show: showEditContactModal,
        hide: hideEditContactModal,
    } = useModal(false);
    const [mergeContactsEditedValues, setMergeContactsEditedValues] = useState<EditedFormValues>();

    const messageModal = useModal(false);

    const { traveler: contact } = useTraveler(contactId, null, {
        enabled: purchase
            ? !isOtherSellerPurchase(purchase, seller.id) && isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW)
            : isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW),
    });

    const customerName = recipientName ?? purchase?.customerName ?? contact?.name ?? "";
    const customerEmail = isEmpty(recipientEmail) ? purchase?.customerEmail ?? contact?.email : recipientEmail;
    const customerPhone = purchase?.phone ?? contact?.phone;

    const messagingPlugin = useSourceMessagingPlugin(purchase?.source);

    // Show the message button if we have a purchase, item and (email, phone, or Airbnb messaging)
    const hasContactInfo = purchase?.customerEmail || (purchase?.phone && isGranted(Permission.SMS_SEND));
    const canMessage = !!hasContactInfo || !!messagingPlugin;
    const showMessageButton = purchase && item && item.seller?.id === getSeller().id && canMessage;

    if ([customerName, customerEmail, customerPhone].every((value) => isEmpty(value))) {
        return null;
    }

    const createConversation = async () => {
        return api.post<Conversation>(`/conversations`, { purchase: { id: purchase!.id }, seller: { id: seller.id } });
    };

    const getConversationId = async () => {
        if (!purchase) {
            return null;
        }

        if (!purchase.conversation?.id) {
            const conversationData = await createConversation();
            return conversationData.data.id;
        }

        return purchase.conversation.id;
    };

    const handleMessageModalClose = async () => {
        messageModal.hide();
        const conversationId = await getConversationId();
        if (conversationId) {
            invalidate("conversations", conversationId);
        }
    };

    const handleMergeContactsSuccess = async () => {
        setMergeContactsEditedValues(undefined);

        if (contact) {
            invalidate("users", contact.id);
        }

        if (purchase) {
            invalidate("purchases", purchase.id);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-lg font-bold">{title}</div>
                {purchase && isPartnerPurchase(purchase) ? null : (
                    <IsGranted permissions={Permission.MODIFY_PURCHASE_CONTACT_VIEW}>
                        <div className="space-x-2">
                            {showMessageButton && (
                                <Tooltip content="Send message to the organizer">
                                    <SecondaryButton
                                        size="small"
                                        data-testid="purchase-customer-information-message-button"
                                        onClick={withPostCheckInWarning(messageModal.show)}
                                    >
                                        <CommentIcon />
                                    </SecondaryButton>
                                </Tooltip>
                            )}

                            {contact && (
                                <IsGranted permissions={Permission.MODIFY_CUSTOMER_DETAILS}>
                                    <Tooltip content="Edit Record">
                                        <SecondaryButton size="small" onClick={showEditContactModal}>
                                            <EditIcon />
                                        </SecondaryButton>
                                    </Tooltip>
                                </IsGranted>
                            )}

                            <Tooltip content="View customer record">
                                <ContactOptionButton onClick={onContactInfoClick ?? toggle} />
                            </Tooltip>
                        </div>
                    </IsGranted>
                )}
            </div>
            <div className="space-y-1">
                {customerName && (
                    <div data-testid="purchase-customer-information-name">
                        <UserIcon className="mr-1" />
                        <NoTranslate>{customerName}</NoTranslate>
                    </div>
                )}
                {customerEmail && (
                    <div data-testid="purchase-customer-information-email">
                        <EmailIcon className="mr-1" />
                        <NoTranslate>{customerEmail}</NoTranslate>
                    </div>
                )}
                {customerPhone && (
                    <div data-testid="purchase-customer-information-phone">
                        <PhoneIcon className="mr-1" />
                        <NoTranslate>
                            <Phone>{customerPhone}</Phone>
                        </NoTranslate>
                    </div>
                )}
            </div>

            {showMessageButton && (
                <>
                    <MessagePurchaseModal
                        isOrganizerOnly
                        description="The message will be sent to organizer only"
                        purchase={purchase}
                        item={item}
                        isOpen={messageModal.isVisible}
                        onClose={handleMessageModalClose}
                        onContinue={handleMessageModalClose}
                    />

                    <PostCheckInWarningModal />
                </>
            )}

            {contact && <ContactInfoDrawer contact={contact} visible={visible} toggle={toggle} />}

            {contact && (
                <EditPrimaryContactModal
                    contact={contact}
                    isOpen={isVisibleEditContactModal}
                    onClose={hideEditContactModal}
                    onDuplicate={(editedValues: EditedFormValues) => setMergeContactsEditedValues(editedValues)}
                />
            )}

            {contact && (
                <MergeContactsModal
                    contact={contact}
                    editedValues={mergeContactsEditedValues}
                    isOpen={!!mergeContactsEditedValues}
                    onClose={() => setMergeContactsEditedValues(undefined)}
                    onSuccess={handleMergeContactsSuccess}
                />
            )}
        </div>
    );
};

const ContactInfoDrawer = ({ contact, visible, toggle }: ContactInfoDrawerProps) => {
    const user = useUser();
    const isAffiliateUser = isAffiliate(user);

    if (isAffiliateUser) {
        return null;
    }

    return <PurchaseContactInfoDrawer isOpen={visible} travelerId={contact.id} onClose={toggle} />;
};

const ContactOptionButton = ({ onClick }: { onClick: () => void }) => {
    const user = useUser();
    const isAffiliateUser = isAffiliate(user);

    if (isAffiliateUser) {
        return null;
    }

    return (
        <SecondaryButton size="small" data-testid="purchase-contact-information-edit-button" onClick={onClick}>
            <UserIcon />
        </SecondaryButton>
    );
};
